<template>
    <div class="bg-gray-100">
        <TopLocationHero title-key="the_project.title" static-image-key="the-project___hero" />

        <div class="relative mt-4 sm:mt-12">
            <div class="container relative z-1">
                <a id="main-content" class="fixed top-0" tabindex="-1"></a>

                <Breadcrumb :items="breadcrumbItems" class="hidden lg:flex" />

                <Type3Block
                    featured
                    separator-below-title
                    title-key="the_project.featured_block.title"
                    description-key="the_project.featured_block.lead"
                    :btn-label="$t('the_project.featured_block.btn_label')"
                    :btn-url="$i18n.locale === 'hu' ? '/liget-budapest-projekt' : '/en/liget-budapest-project'"
                    :secondary-btn-label="$t('the_project.featured_block.secondary_btn_label')"
                    :secondary-btn-url="localePath('the-project-our-first-10-years')"
                    static-image-key="the-project___intro"
                />

                <div class="grid grid-cols-12 base-responsive-gap mt-16 sm:mt-20">
                    <div class="col-span-12 lg:col-span-8">
                        <h2 class="header-1"><T t="the_project.institutions_block.title" /></h2>
                        <p class="separator-border-top-line mt-4">
                            <T t="the_project.institutions_block.lead" />
                        </p>
                    </div>
                </div>
            </div>

            <div
                class="z-0 absolute top-0 xl:top-32 3xl:top-44 left-0 w-full h-[200vh] overflow-hidden"
                style="transform: rotateY(180deg)"
            >
                <div
                    class="hidden sm:block z-2 bg-illustration top-20 lg:top-40 2xl:top-32 wave-bg-responsive-parameters opacity-30"
                    v-lazy-background :lazy-background="require('~/assets/images/shapes/shape-3_gray-500.png')"
                ></div>
            </div>

            <div
                class="absolute bg-contain bg-center bg-no-repeat bottom-32 left-0 w-1/2 h-[65rem] z-0"
                style="background-position: top right; transform: rotateY(180deg) translateY(100%)"
                v-lazy-background :lazy-background="require('~/assets/images/shapes/wave-lines.svg')"
            ></div>
        </div>

        <div v-if="institutions.length" class="relative overflow-hidden mt-12 lg:mt-20 pb-20 lg:pb-40">
            <PlaceGridBlock :items="institutions" />

            <div
                class="z-1 absolute bg-cover sm:bg-contain bg-no-repeat w-full h-screen sm:h-[300vh] bottom-0 left-0 opacity-40"
                style="background-position: bottom center; filter: grayscale(100%)"
                v-lazy-background :lazy-background="require('~/assets/images/shapes/gradients/rasterized-gradient-blue-500.png')"
            ></div>
        </div>

        <div class="relative z-1">
            <div
                class="z-0 absolute w-full h-screen pt-8 sm:pt-16 bg-contain lg:bg-cover bg-no-repeat mt-[-6vw] lg:mt-[-9vw] xl:mt-[-7.5vw] 2xl:mt-[-6vw] 3xl:mt-[-5.5vw]"
                style="background-position: top center"
                v-lazy-background :lazy-background="require('~/assets/images/shapes/waves/wave-1-yellow-400.png')"
            ></div>

            <div class="container relative z-1 pt-4 lg:pt-12">
                <div class="grid grid-cols-12 base-responsive-gap">
                    <div class="col-span-12 lg:col-span-4 flex items-center lg:justify-center">
                        <div
                            class="relative lg:absolute bg-contain bg-center bg-no-repeat w-52 h-16 lg:w-80 lg:h-20"
                            v-lazy-background :lazy-background="require('~/assets/images/illustrations/dog-tunnel.svg')"
                        ></div>
                    </div>
                    <div class="col-span-12 lg:col-span-8">
                        <h2 class="header-1"><T t="the_project.renewing_park.title" /></h2>
                        <p class="separator-border-top-line mt-4">
                            <T t="the_project.renewing_park.description" />
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="parks.length" class="relative overflow-hidden mt-12 lg:mt-20 pb-14 lg:pb-40">
                <PlaceGridBlock :items="parks" partial-initial-display />
            </div>
        </div>

        <div v-if="planned.length" class="relative z-1">
            <div
                class="z-0 absolute w-full h-screen pt-8 sm:pt-16 bg-contain lg:bg-cover bg-no-repeat mt-[-6vw] lg:mt-[-9vw] xl:mt-[-7.5vw] 2xl:mt-[-6vw] 3xl:mt-[-5.5vw]"
                style="background-position: top center"
                v-lazy-background :lazy-background="require('~/assets/images/shapes/shape-7_blue-300.png')"
            ></div>

            <div class="container relative z-1 pt-4 lg:pt-12">
                <div class="grid grid-cols-12 base-responsive-gap">
                    <div class="col-span-12 lg:col-span-8">
                        <h2 class="header-1"><T t="the_project.planned_projects.title" /></h2>
                        <div class="separator-border-top-line mt-4"></div>
                    </div>
                </div>
            </div>

            <div class="relative overflow-hidden mt-12 lg:mt-20 pb-14 lg:pb-40">
                <PlaceGridBlock :items="planned" partial-initial-display />
            </div>
        </div>

        <VideoGalleryBlock v-if="videos.length" :videos="videos" />
    </div>
</template>

<script>
import TopLocationHero from '@/components/Pages/TopLocation/Hero/Hero.vue';
import Type3Block from '@/components/Blocks/Type3Block/Type3Block.vue';
import PlaceGridBlock from '@/components/Blocks/PlaceGridBlock/PlaceGridBlock.vue';
import VideoGalleryBlock from '@/components/Blocks/VideoGalleryBlock/VideoGalleryBlock.vue';

export default {
    name: 'TheProjectIndex',
    components: {
        Type3Block,
        TopLocationHero,
        PlaceGridBlock,
        VideoGalleryBlock,
    },
    async asyncData({ $axios, i18n }) {
        let institutions = [];
        let parks = [];
        let planned = [];
        let videos = [];

        await Promise.allSettled([
            $axios.$get('/institutions?type=institution&is_planned_only=0'),
            $axios.$get('/institutions?type=park&is_planned_only=0'),
            $axios.$get('/institutions?is_planned_only=1'),
            $axios.$get('/videos'),
        ])
            .then(([institutionsRes, parksRes, plannedRes, videosRes]) => {
                if (institutionsRes.status === 'fulfilled') {
                    institutions = institutionsRes.value.data;
                }

                if (parksRes.status === 'fulfilled') {
                    parks = parksRes.value.data;
                }

                if (plannedRes.status === 'fulfilled') {
                    planned = plannedRes.value.data;
                }

                if (videosRes.status === 'fulfilled') {
                    videos = videosRes.value.data;
                }
            })
            .catch((err) => {
                console.log(err);
            });

        const breadcrumbItems = [];

        breadcrumbItems.push([i18n.t('the_project.title'), 'the-project']);

        return {
            breadcrumbItems,
            institutions,
            parks,
            planned,
            videos,
        };
    },
    head() {
        return {
            title: this.$getPageTitle('the-project.index'),
            meta: this.$getPageMetaData('the-project.index'),
        };
    },
};
</script>

<style scoped></style>
